import React from "react";
import { List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import { ShortcutGroupToShortcuts } from "modules/shortcuts/models";
import LinkIcon from '@mui/icons-material/Link';
import { loggedEventsApi } from "api/instances";

import "../../styles/shortcutsFeedList.sass";

const MAX_URL_DISPLAY_LENGTH = 35;

interface IShortcutsFeedListProps {
    shortcutsList: ShortcutGroupToShortcuts[];
    delayShowListItemsInMs?: number; // for setting the delay in ms for showing the list items. If this is set, you must also set the isListShowing prop
    isListShowing?: boolean; // paired with the delay, this is to ensure that the list will immediately dissapear if the parent component isn't showing anymore
}

const ShortcutsFeedList: React.FC<IShortcutsFeedListProps> = ({
    shortcutsList,
    delayShowListItemsInMs,
    isListShowing
}) => {
    const getDisplayUrl = (url: string) => url.length > MAX_URL_DISPLAY_LENGTH
        ? url.slice(0, MAX_URL_DISPLAY_LENGTH - 3) + "..."
        : url;

    const [contentVisible, setContentVisible] = React.useState<boolean>(delayShowListItemsInMs === undefined);

    React.useEffect(() => {
        if (delayShowListItemsInMs !== undefined && isListShowing !== undefined) {
            isListShowing 
                ? setTimeout(() => setContentVisible(true), delayShowListItemsInMs)
                : setContentVisible(false);
        }
    }, [delayShowListItemsInMs, isListShowing]);

    const onClick = async (shortcutId: string) => {
        await loggedEventsApi.sendShortcutOpenedEvent(shortcutId);
    }
    
    return <List dense>
        {
            contentVisible &&
            shortcutsList
                .filter(sgts => sgts.shortcuts.length > 0)
                .map(sgts =>
                    <React.Fragment key={sgts.shortcutGroup.id}>
                        <ListItem key={sgts.shortcutGroup.id}>
                            <ListItemText primaryTypographyProps={{ className: "shortcut-group-display-name" }}>
                                {sgts.shortcutGroup.name}
                            </ListItemText>
                        </ListItem>
                        {
                            sgts.shortcuts.map(s =>
                                <ListItem key={s.id} className="shortcut-item">
                                    <ListItemIcon className="icon-div"><LinkIcon /></ListItemIcon>
                                    <ListItemText
                                        className="shortcut-display-name"
                                        primary={s.name}
                                        primaryTypographyProps={{ className: "shortcut-display-name" }}
                                        secondary={
                                            <a 
                                                href={s.url} 
                                                className="shortcut-display-url"
                                                target={s.openInNewWindow ? "_blank" : undefined}
                                                onClick={() => onClick(s.id)}
                                            >
                                                {getDisplayUrl(s.url)}
                                            </a>
                                        }
                                    />
                                </ListItem>
                            )
                        }
                    </React.Fragment>
                )
        }
    </List>;
};

export default ShortcutsFeedList;
